import React from 'react';
import { format, differenceInDays, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  ArrowLeft,
  Check,
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  Send,
  Users
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { CampaignDetail, CampaignStatus, HsmTemplate } from '../../types';

// Helper to get badge variant for campaign status
function getCampaignStatusBadge(status: CampaignStatus) {
  const statusMap: Record<CampaignStatus, { variant: 'secondary' | 'active' | 'paused' | 'completed'; label: string }> = {
    DRAFT: { variant: 'secondary', label: 'Rascunho' },
    ACTIVE: { variant: 'active', label: 'Ativa' },
    PAUSED: { variant: 'paused', label: 'Pausada' },
    COMPLETED: { variant: 'completed', label: 'Concluida' }
  };
  const config = statusMap[status] || { variant: 'secondary' as const, label: status };
  return <Badge variant={config.variant}>{config.label}</Badge>;
}

// Helper to format date range
function formatDateRange(startDate: string, endDate: string): string {
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  return `${format(start, 'dd/MM/yyyy', { locale: ptBR })} - ${format(end, 'dd/MM/yyyy', { locale: ptBR })}`;
}

// Helper to calculate campaign duration in days
function getCampaignDuration(startDate: string, endDate: string): number {
  return differenceInDays(parseISO(endDate), parseISO(startDate)) + 1;
}

export interface CampaignDetailViewProps {
  campaign: CampaignDetail;
  loading: boolean;
  approvedTemplates: HsmTemplate[];
  runningCampaignId: number | null;
  restartSuccessMessage: string | null;
  runSuccessMessage: string | null;
  onBack: () => void;
  onStartCampaign: () => void;
  onPauseCampaign: () => void;
  onResumeCampaign: () => void;
  onCompleteCampaign: () => void;
  onRestartCampaign: () => void;
  onRunCampaign: () => void;
  onAddContacts: () => void;
}

export function CampaignDetailView({
  campaign,
  loading,
  approvedTemplates,
  runningCampaignId,
  restartSuccessMessage,
  runSuccessMessage,
  onBack,
  onStartCampaign,
  onPauseCampaign,
  onResumeCampaign,
  onCompleteCampaign,
  onRestartCampaign,
  onRunCampaign,
  onAddContacts
}: CampaignDetailViewProps) {
  return (
    <div className="space-y-4">
      {/* Header with back button */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">{campaign.name}</h2>
            {getCampaignStatusBadge(campaign.status)}
          </div>
          <p className="text-sm text-muted-foreground">
            {formatDateRange(campaign.startDate, campaign.endDate)}
          </p>
        </div>
        <div className="flex gap-2">
          {campaign.status === 'DRAFT' && (
            <Button variant="default" onClick={onStartCampaign}>
              <Play className="mr-2 h-4 w-4" />
              Iniciar Campanha
            </Button>
          )}
          {campaign.status === 'ACTIVE' && (
            <>
              <Button
                variant="default"
                onClick={onRunCampaign}
                disabled={runningCampaignId === campaign.id}
              >
                <Send className="mr-2 h-4 w-4" />
                {runningCampaignId === campaign.id ? 'Executando...' : 'Executar Campanha'}
              </Button>
              <Button variant="outline" onClick={onPauseCampaign}>
                <Pause className="mr-2 h-4 w-4" />
                Pausar
              </Button>
            </>
          )}
          {campaign.status === 'PAUSED' && (
            <Button variant="default" onClick={onResumeCampaign}>
              <Play className="mr-2 h-4 w-4" />
              Retomar
            </Button>
          )}
          {(campaign.status === 'ACTIVE' || campaign.status === 'PAUSED') && (
            <Button variant="outline" onClick={onCompleteCampaign}>
              <Check className="mr-2 h-4 w-4" />
              Concluir
            </Button>
          )}
          {campaign.status === 'COMPLETED' && (
            <Button variant="default" onClick={onRestartCampaign}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Reiniciar Campanha
            </Button>
          )}
        </div>
      </div>

      {/* Success message toast for detail view */}
      {restartSuccessMessage && (
        <div className="fixed bottom-4 right-4 z-50 animate-in fade-in slide-in-from-bottom-4">
          <div className="rounded-lg border border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800 px-4 py-3 shadow-lg">
            <p className="text-sm text-green-700 dark:text-green-300">{restartSuccessMessage}</p>
          </div>
        </div>
      )}
      {runSuccessMessage && (
        <div className="fixed bottom-4 right-4 z-50 animate-in fade-in slide-in-from-bottom-4">
          <div className="rounded-lg border border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800 px-4 py-3 shadow-lg">
            <p className="text-sm text-green-700 dark:text-green-300">{runSuccessMessage}</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{campaign.contactCount ?? campaign.contacts.length}</div>
                <div className="text-xs text-muted-foreground">Contatos Inscritos</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">
                  {getCampaignDuration(campaign.startDate, campaign.endDate)}
                </div>
                <div className="text-xs text-muted-foreground">Dias de Campanha</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{campaign.messagesSent ?? 0}</div>
                <div className="text-xs text-muted-foreground">Mensagens Enviadas</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{campaign.totalMessages ?? 0}</div>
                <div className="text-xs text-muted-foreground">Total de Mensagens</div>
              </CardContent>
            </Card>
          </div>

          {/* Progress Bar */}
          {campaign.totalMessages !== undefined && campaign.totalMessages > 0 && (
            <Card>
              <CardContent className="pt-6">
                <div className="flex justify-between text-sm mb-2">
                  <span>Progresso da Campanha</span>
                  <span>
                    {Math.round(((campaign.messagesSent ?? 0) / campaign.totalMessages) * 100)}%
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{
                      width: `${((campaign.messagesSent ?? 0) / campaign.totalMessages) * 100}%`
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Template Assignments */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Templates da Campanha</CardTitle>
              <CardDescription>
                {campaign.templateMode === 'SAME_FOR_ALL'
                  ? 'Mesmo template para todos os dias'
                  : 'Template diferente por dia'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {campaign.templateMode === 'SAME_FOR_ALL' ? (
                <div className="p-3 rounded-lg bg-muted/50">
                  <div className="text-sm font-medium">
                    Template: {approvedTemplates.find(t => t.id === campaign.defaultTemplateId)?.name || campaign.defaultTemplateId || 'Nao definido'}
                  </div>
                </div>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {campaign.dayTemplates.map((dt) => (
                    <div key={dt.id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                      <div className="min-w-[80px]">
                        <div className="text-sm font-medium">Dia {dt.dayNumber}</div>
                        <div className="text-xs text-muted-foreground">
                          {format(parseISO(dt.date), 'dd/MM', { locale: ptBR })}
                        </div>
                      </div>
                      <div className="flex-1 text-sm">
                        {dt.templateName || approvedTemplates.find(t => t.id === dt.templateId)?.name || dt.templateId}
                      </div>
                    </div>
                  ))}
                  {campaign.dayTemplates.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Nenhum template configurado.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Enrolled Contacts */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Contatos Inscritos</CardTitle>
                  <CardDescription>
                    {campaign.contacts.length} contato{campaign.contacts.length !== 1 ? 's' : ''} na campanha
                  </CardDescription>
                </div>
                {campaign.status !== 'COMPLETED' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onAddContacts}
                  >
                    <Users className="mr-2 h-4 w-4" />
                    Adicionar Contatos
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="max-h-60 overflow-y-auto space-y-1">
                {campaign.contacts.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Nenhum contato inscrito nesta campanha.
                  </p>
                ) : (
                  campaign.contacts.map((cc) => (
                    <div key={cc.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {cc.name || cc.phoneNumber || `Contato #${cc.contactId}`}
                        </div>
                        {cc.name && (
                          <div className="text-xs text-muted-foreground">{cc.phoneNumber}</div>
                        )}
                        {cc.company && (
                          <div className="text-xs text-muted-foreground">{cc.company}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={
                          cc.status === 'COMPLETED' ? 'completed' :
                          cc.status === 'IN_PROGRESS' ? 'active' :
                          cc.status === 'OPTED_OUT' ? 'optedOut' : 'pending'
                        }>
                          {cc.status === 'PENDING' ? 'Pendente' :
                           cc.status === 'IN_PROGRESS' ? 'Em Progresso' :
                           cc.status === 'COMPLETED' ? 'Concluido' : 'Opt-out'}
                        </Badge>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
